import { BUNDLE_TYPE } from "utils/constants";
import extractIdFromGid from "helpers/extract-gid";


export const cloneLineItems = (lines) => {
	if (!lines || typeof lines !== "object") return {};

	return Object.keys(lines).reduce((acc, key) => {
		const item = lines[key];
		if (!item) return acc;

		const clone = {
			...item,
			duplicates: Array.isArray(item.duplicates) ? item.duplicates.map(duplicate => ({ ...duplicate })) : [],
			attributes: Array.isArray(item.attributes) ? item.attributes.map(attribute => ({ ...attribute })) : item.attributes,
			customAttributes: Array.isArray(item.customAttributes) ?
				item.customAttributes.map(attribute => ({ ...attribute })) :
				item.customAttributes,
		};

		acc[key] = clone;
		return acc;
	}, {});
};


const toNumber = (val, fallback = 0) => {
	if (val === null || val === undefined) return fallback;
	if (typeof val === "number") return val;
	if (typeof val === "string") return Number(val) || fallback;
	// Handle nested object like {amount: 0} - don't use || fallback since 0 is valid
	if (typeof val === "object" && "amount" in val) {
		const num = Number(val.amount);
		return isNaN(num) ? fallback : num;
	}
	return fallback;
};

const getAttr = (attributes, key) => {
	if (!Array.isArray(attributes)) return undefined;
	return attributes.find((attr) => attr.key === key)?.value;
};

const bundleRequestCache = new Map();

const fetchBundleDefinition = async (bundleId) => {
	const normalizedBundleId = bundleId ? String(bundleId) : "";
	if (!normalizedBundleId) return null;

	const cachedRequest = bundleRequestCache.get(normalizedBundleId);
	if (cachedRequest) return cachedRequest;

	const request = (async () => {
		try {
			const response = await fetch("/.netlify/functions/fetch-bundle", {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
				},
				body: JSON.stringify({
					bundleId: normalizedBundleId,
				})
			});

			const data = await response.json();
			return data?.bundle || null;
		} catch (error) {
			console.log(error);
			return null;
		}
	})();

	bundleRequestCache.set(normalizedBundleId, request);

	const bundle = await request;
	if (!bundle)
		bundleRequestCache.delete(normalizedBundleId);

	return bundle;
};

const mapLine = (line, index, variantWeights = {}) => {
	if (!line) return null;

	// Support both camelCase and snake_case API responses
	const variantId = extractIdFromGid(line.variantId || line.variant_id);
	if (!variantId) return null;

	const productId = extractIdFromGid(line.productId || line.product_id);
	const quantity = Number(line.quantity) || 0;

	// Get current price (snake_case: current_price, camelCase: currentPrice)
	const currentPriceVal = line.current_price || line.currentPrice;
	const unitBasePrice = toNumber(currentPriceVal) ||
		(quantity > 0 ? toNumber(line.linePrice ?? line.originalLinePrice ?? line.originalPrice) / quantity : 0);

	const linePriceTotal = quantity > 0 ?
		unitBasePrice * quantity :
		unitBasePrice;

	// Get discounted price (snake_case: line_discounted_price, camelCase: lineDiscountedPrice)
	const lineDiscountedPriceVal = line.line_discounted_price || line.lineDiscountedPrice;
	const rawDiscountedTotal = toNumber(
		lineDiscountedPriceVal ??
		line.discountedPrice ??
		line.discountedTotal
	);

	// Check if a discounted price was explicitly provided (including 0 for 100% discount)
	const hasDiscountedPrice =
		lineDiscountedPriceVal !== undefined && lineDiscountedPriceVal !== null ||
		line.discountedPrice !== undefined && line.discountedPrice !== null ||
		line.discountedTotal !== undefined && line.discountedTotal !== null;

	const lineDiscountedTotal = hasDiscountedPrice ?
		rawDiscountedTotal :
		linePriceTotal;

	const unitDiscountedPrice = quantity > 0 ?
		(lineDiscountedTotal / quantity) :
		lineDiscountedTotal;

	const attributes = line.customAttributes || line.custom_attributes || [];

	// Bundle-related attributes
	const bundleId = getAttr(attributes, "_bid");
	const sanityBundleId = getAttr(attributes, "_sdid");
	const bundleQty = getAttr(attributes, "_bqty");
	const subproductQty = getAttr(attributes, "__qty");

	return {
		id: line.id,
		index,
		variantShopifyId: variantId,
		productId,
		quantity,

		// pricing
		price: unitBasePrice,
		basePrice: unitBasePrice,
		discountedPrice: unitDiscountedPrice,
		unitDiscountedPrice,
		linePrice: linePriceTotal || (unitBasePrice * quantity),
		lineDiscountedPrice: lineDiscountedTotal,

		// quantity factor for bundle items only
		__quantity: 1,

		// attributes
		attributes,
		customAttributes: attributes,

		// bundle attributes
		bundleId: bundleId ? extractIdFromGid(bundleId) : null,
		sanityBundleId: sanityBundleId || null,
		bundleQty: bundleQty ? Number(bundleQty) : null,
		subproductQty: subproductQty ? Number(subproductQty) : null,

		image: line.variantImage || line.variant_image || null,
		name: line.title || "",
		sku: line.sku || null,

		weightInGrams: variantWeights[variantId],

		raw: line,
	};
};

const determineType = (item) => {
	if (item.weightInGrams < 1000) {
		return "Add-on";
	} else if (item.weightInGrams > 2000) {
		return BUNDLE_TYPE;
	} else {
		return "Bowl";
	}
};

// eslint-disable-next-line no-unused-vars
export const mapEversubsSubscriptionLines = async (lines = [], discounts = [], variantWeights = {}) => {
	const linesByVariant = {};
	const bundles = {};
	let totalLineItemPrice = 0;
	let totalLineItemDiscountedPrice = 0;
	let totalWeight = 0;

	for (let i = 0; i < lines.length; i++) {
		const mapped = mapLine(lines[i], i, variantWeights);
		if (!mapped) continue;

		const { variantShopifyId, discountedPrice } = mapped;
		const bundleId = mapped.attributes.find((attr) => attr.key === "_bid")?.value;
		const type = determineType(mapped);

		if (bundleId) {
			let existingBundle = bundles[bundleId];

			if (!existingBundle) {
				const sanityBundleId = mapped.attributes?.find((attr) => attr.key === "_sdid")?.value;

				const bundle = await fetchBundleDefinition(sanityBundleId);

				if (!bundle) continue;

				const mappedItemRef = bundle.products_included.find((subp) => {
					const subProductId = subp._id.split("__")[0];

					+subProductId === +mapped.productId;
				});

				let bundleQty = 1;

				if (mappedItemRef) {
					const { __quantity } = mappedItemRef;

					bundleQty = mapped.quantity > 0 ? mapped.quantity / +(__quantity) : 1;
				}

				const bundleWeightInGrams = (bundle.products_included || []).reduce((acc, subprod) => {
					const subWeight = subprod?.variant?.weight ? subprod.variant.weight * 1000 : 0;
					const subQty = subprod.__quantity || 1;
					return acc + (subWeight * subQty);
				}, 0) || (bundle.variant.weight === 0 ? 1 : bundle?.variant?.weight * 1000);

				existingBundle = bundles[bundleId] = {
					...bundle,
					id: +bundleId,
					duplicates: [],
					type: BUNDLE_TYPE,
					variantShopifyId: +bundleId,
					productShopifyId: +String(bundle._id).split("__")[0],
					updating: false,
					price: bundle?.basePrice || bundle.variant.price,
					quantity: bundleQty,
					totalQuantity: bundleQty,
					index: i,
					weightInGrams: bundleWeightInGrams,
					name: bundle.shopifyTitle || bundle.title,
					discountedPrice: bundle.variant.price * bundleQty,
					isDuplicated: false,
					products_included: [],
				};

			}

			mapped.type = type;
			mapped.inBundle = true;

			existingBundle.products_included.push(mapped);

			linesByVariant[bundleId] = existingBundle;

			const subQty = mapped.quantity || 0;
			const subWeightInGrams = mapped.weightInGrams || 0;
			const subWeightUnits = subWeightInGrams >= 1000 ? subWeightInGrams / 1000 : (subWeightInGrams <= 1 ? 0 : subWeightInGrams);
			totalWeight += subWeightUnits * subQty;

			const subLineBaseTotal = mapped.linePrice !== undefined ? mapped.linePrice : (mapped.price || 0) * subQty;
			const subLineDiscountedTotal = mapped.lineDiscountedPrice !== undefined
				? mapped.lineDiscountedPrice
				: (mapped.discountedPrice || 0) * subQty;
			totalLineItemPrice += Number.isFinite(subLineBaseTotal) ? subLineBaseTotal : 0;
			totalLineItemDiscountedPrice += Number.isFinite(subLineDiscountedTotal) ? subLineDiscountedTotal : 0;

			continue;
		}
		// else if (type === BUNDLE_TYPE) {
		// 	let bundle = null;
		//
		// 	try {
		// 		const response = await fetch("/.netlify/functions/fetch-bundle", {
		// 			method: "POST",
		// 			headers: {
		// 				"Content-Type": "application/json",
		// 			},
		// 			body: JSON.stringify({
		// 				bundleId: mapped?.productId,
		// 			})
		// 		});
		//
		// 		const data = await response.json();
		//
		// 		if (data.bundle) bundle = data.bundle;
		// 	} catch (error) {
		// 		console.log(error);
		// 	}
		//
		// 	mapped.products_included = !bundle?.products_included
		// 		? []
		// 		: bundle.products_included.map((subprod) => {
		// 			return {
		// 				attributes: [],
		// 				basePrice: subprod.variant.price,
		// 				bundleTransactionId: 0,
		// 				discountedPrice: (subprod.variant.price - productDiscountValue) * mapped.quantity,
		// 				id: Math.random(),
		// 				image: subprod.image,
		// 				isOneTimeAdded: false,
		// 				isOneTimeRemoved: false,
		// 				name: subprod.title,
		// 				productTitle: subprod.title,
		// 				inBundle: true,
		// 				type: determineType(subprod),
		// 				compareAtPrice: subprod.variant.compareAtPrice,
		// 				sku: subprod.variant.sku,
		// 				quantity: subprod.__quantity * mapped.quantity,
		// 				price: subprod.variant.price,
		// 				variantShopifyId: subprod.variant.id,
		// 				productShopifyId: subprod._id,
		// 				weightInGrams: !subprod.variant.weight ? 0.1 : subprod.variant.weight * 1000,
		// 				__quantity: subprod.__quantity,
		// 			};
		// 		});
		// }

		if (linesByVariant[variantShopifyId]) {
			linesByVariant[variantShopifyId].isDuplicated = true;
			linesByVariant[variantShopifyId].duplicates.push(mapped);
			linesByVariant[variantShopifyId].discountedPrice = linesByVariant[variantShopifyId].discountedPrice + discountedPrice;
			linesByVariant[variantShopifyId].totalQuantity = linesByVariant[variantShopifyId].totalQuantity + mapped.quantity;
		} else {
			linesByVariant[variantShopifyId] = {
				...mapped,
				duplicates: [],
				updating: false,
				totalQuantity: mapped.quantity,
				type,
				index: i,
			};
		}

		const qty = mapped.quantity || 0;
		const weightInGrams = mapped.weightInGrams || 0;
		const weightUnits = weightInGrams >= 1000 ?
			weightInGrams / 1000 :
			weightInGrams <= 1 ? 0 : weightInGrams;

		const lineBaseTotal = mapped.linePrice !== undefined ? mapped.linePrice : (mapped.price || 0) * qty;
		const lineDiscountedTotal = mapped.lineDiscountedPrice !== undefined ?
			mapped.lineDiscountedPrice :
			(mapped.discountedPrice || 0) * qty;

		totalLineItemPrice += Number.isFinite(lineBaseTotal) ? lineBaseTotal : 0;
		totalLineItemDiscountedPrice += Number.isFinite(lineDiscountedTotal) ? lineDiscountedTotal : 0;
		totalWeight += weightUnits * qty;
	}

	return {
		linesByVariant,
		totalLineItemPrice,
		totalLineItemDiscountedPrice,
		totalWeight,
	};
};
